import { Form, useForm } from '@/components/forms';
import { Stepper } from '@/components/Stepper';
import { Button } from '@/design-system/button';
import { ClubInfo as ClubInfoForm } from '@/features/store-management/premium-stores/form/ClubInfo';
import { Translation } from '@/hoslog-intl';
import { resolveUpload } from '@/lib/resolveUpload';
import { BasicStores } from '@/services/basicStores/basicStores.types';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BasicInfo } from '../premium-stores/BasicInfo';
import { ClubInfo } from '../premium-stores/ClubInfo';
import { ClubAddress } from '../premium-stores/form/ClubAddress';
import { Others } from '../premium-stores/form/Others';
import { formSchema } from '../premium-stores/premiumStore.schema';
import { STORE_SETUP_STEPS } from '../premium-stores/premiumStores.constant';
import { SNS } from '../Sns';
import useSwitchStoreMutation from './hooks/useSwitchStoreMutation';

const DEFAULT_STEP = 1;
type ConvertBasicToPremiumProps = {
  getBasicStoreDetails: BasicStores | undefined;
  id: string;
};

const ConvertBasicToPremium = ({ getBasicStoreDetails, id }: ConvertBasicToPremiumProps) => {
  const [activeStep, setActiveStep] = useState(DEFAULT_STEP);
  const [passportImage, setPassportImage] = useState('');
  const [insuranceImage, setInsuranceImage] = useState('');
  const [businessImage, setBusinessImage] = useState('');
  const [corporateRegistryImage, setCorporateRegistryImage] = useState('');
  const [kycFrontImage, setKycFrontImage] = useState('');
  const [kycBackImage, setKycBackImage] = useState('');

  const defaultValues = {
    username: getBasicStoreDetails?.username || '',
    profileImage: getBasicStoreDetails?.profileImage?.path || '',
    coverImage: getBasicStoreDetails?.coverImage?.path || '',
    storeName: getBasicStoreDetails?.storeName || '',
    storeNameFurigana: getBasicStoreDetails?.storeNameFurigana || '',
    groupName: getBasicStoreDetails?.groupName || '',
    description: getBasicStoreDetails?.description || '',
    businessType: 'default',
    ceoFullName: '',
    ceoFurigana: '',
    companyAddress: '',
    landlineNumber: getBasicStoreDetails?.landlineNumber || '',
    mobileNumber: '',
    emailAddress: '',
    longitude: '',
    latitude: '',
    postalCode: getBasicStoreDetails?.postalCode || '',
    prefecture: getBasicStoreDetails?.prefecture || '',
    city: getBasicStoreDetails?.city || '',
    street: getBasicStoreDetails?.street || '',
    buildingNo: getBasicStoreDetails?.buildingNo || '',
    passportImage: '',
    insuranceCardImage: '',
    businessCertificateImage: '',
    corporateRegistryImage: '',
    identificationType: 'default',
    kycFront: '',
    kycBack: '',
    businessLicenseNumber: '',
    licenseIssueDate: '',
    workingTimeFrom: getBasicStoreDetails?.workingTimeFrom || '',
    workingTimeTo: getBasicStoreDetails?.workingTimeTo || '',
    lastEntryTime: getBasicStoreDetails?.lastEntryTime || '',
    holidays: getBasicStoreDetails?.holidays || '',
    tax: getBasicStoreDetails?.tax || '',
    closingDate: getBasicStoreDetails?.closingDate || '',
    otherHolidays: getBasicStoreDetails?.otherHolidays || [],
    instagram: getBasicStoreDetails?.instagram || '',
    tiktok: getBasicStoreDetails?.tiktok || '',
    line: getBasicStoreDetails?.line || '',
    twitter: getBasicStoreDetails?.twitter || '',
    website: getBasicStoreDetails?.website || '',
    googleMap: getBasicStoreDetails?.googleMap || '',
  };

  const form = useForm({
    schema: formSchema,
    mode: 'onBlur',
    defaultValues: defaultValues,
  });
  const [profileImage, setProfileImage] = useState<string | undefined>('');
  const [coverImage, setCoverImage] = useState<string | undefined>('');
  const { mutateAsync } = useSwitchStoreMutation();

  useEffect(() => {
    setProfileImage(getBasicStoreDetails?.profileImage?.path);
    setCoverImage(getBasicStoreDetails?.coverImage?.path);
  }, []);

  const handleNext = async () => {
    const formData = form.getValues();

    const stepConfig = STORE_SETUP_STEPS.find((s) => s.step === activeStep);

    const isValid = await form.trigger(stepConfig?.fields, { shouldFocus: true });

    const profileImageFile = formData.profileImage?.valueOf();
    const coverImageFile = formData.coverImage?.valueOf();

    if (!profileImageFile) {
      toast.error('Profile image is required');
      return;
    }
    if (!coverImageFile) {
      toast.error('Cover image is required');
      return;
    }

    if (!isValid) return;

    setActiveStep((prev) => prev + 1);
  };

  return (
    <div className="rounded-lg bg-card shadow-panel p-8">
      <Stepper
        items={STORE_SETUP_STEPS}
        value={activeStep}
        defaultActive={DEFAULT_STEP}
        onValueChange={setActiveStep}
      />
      <Form
        form={form}
        onSubmit={async () => {
          try {
            const formData = form.getValues();

            const [
              profileImageUrl,
              coverImageUrl,
              passportImageUrl,
              insuranceCardImageUrl,
              businessCertificateImageUrl,
              corporateRegistryImageUrl,
              kycFrontUrl,
              kycBackUrl,
            ] = await Promise.all([
              resolveUpload(formData.profileImage),
              resolveUpload(formData.coverImage),
              resolveUpload(formData.passportImage),
              resolveUpload(formData.insuranceCardImage),
              resolveUpload(formData.businessCertificateImage),
              resolveUpload(formData.corporateRegistryImage),
              resolveUpload(formData.kycFront),
              resolveUpload(formData.kycBack),
            ]);

            const payload = {
              id: id,
              profileImage: profileImageUrl || '',
              coverImage: coverImageUrl || '',
              storeName: formData.storeName || '',
              storeNameFurigana: formData.storeNameFurigana || '',
              groupName: formData.groupName || '',
              description: formData.description || '',
              businessType: formData.businessType || '',
              ceoFullName: formData.ceoFullName || '',
              ceoFurigana: formData.ceoFurigana || '',
              companyName: formData.companyName || '',
              companyNameFurigana: formData.companyNameFurigana || '',
              companyAddress: formData.companyAddress || '',
              landlineNumber: formData.landlineNumber || '',
              mobileNumber: formData.mobileNumber || '',
              emailAddress: formData.emailAddress || '',
              username: formData.username || '',
              identificationType: formData.identificationType || '',
              passportImage: passportImageUrl || '',
              insuranceCardImage: insuranceCardImageUrl || '',
              businessCertificateImage: businessCertificateImageUrl || '',
              kycFrontImage: kycFrontUrl || '',
              kycBackImage: kycBackUrl || '',
              corporateRegistryImage: corporateRegistryImageUrl || '',
              businessLicenseNumber: formData.businessLicenseNumber || '',
              licenseIssueDate: formData.licenseIssueDate ? new Date(formData.licenseIssueDate).getTime() : 0,
              longitude: formData.longitude || '',
              latitude: formData.latitude || '',
              postalCode: formData.postalCode || '',
              prefecture: formData.prefecture || '',
              city: formData.city || '',
              street: formData.street || '',
              buildingNo: formData.buildingNo || '',
              workingTimeFrom: formData.workingTimeFrom || '',
              workingTimeTo: formData.workingTimeTo || '',
              lastEntryTime: formData.lastEntryTime || '',
              holidays: formData.holidays || '',
              tax: formData.tax || '',
              closingDate: formData.closingDate || '',
              otherHolidays: formData.otherHolidays || [],
              instagram: formData.instagram || '',
              tiktok: formData.tiktok || '',
              line: formData.line || '',
              twitter: formData.twitter || '',
              website: formData.website || '',
              googleMap: formData.googleMap || '',
            };
            await mutateAsync(payload);
          } catch {
            // The mutation displays the user-facing submission error.
          }
        }}
      >
        <div className={activeStep === 1 ? 'block' : 'hidden'}>
          <BasicInfo
            profileImagePath={profileImage}
            setProfileImage={setProfileImage}
            coverImagePath={coverImage}
            setCoverImage={setCoverImage}
            passportImagePath={passportImage}
            setPassportImage={setPassportImage}
            insuranceImagePath={insuranceImage}
            setInsuranceImage={setInsuranceImage}
            businessImagePath={businessImage}
            setBusinessImage={setBusinessImage}
            corporateRegistryImagePath={corporateRegistryImage}
            setCorporateRegistryImage={setCorporateRegistryImage}
            kycFrontImage={kycFrontImage}
            setKycFrontImage={setKycFrontImage}
            kycBackImage={kycBackImage}
            setKycBackImage={setKycBackImage}
          />
        </div>
        <div className={activeStep === 2 ? 'block' : 'hidden'}>
          <ClubInfo>
            <div className="w-full flex flex-col gap-6 mt-8">
              <ClubAddress />
              <ClubInfoForm />
              <SNS storeType="premium" />
              <Others />
            </div>
          </ClubInfo>
        </div>
        <div className="flex w-full gap-4 text-sm mt-6">
          {activeStep === 2 ? (
            <div className="flex justify-between w-full">
              <Link to="/store-information/basic-stores">
                <Button variant="outline" className="w-[122px]">
                  <Translation id="common.page.button.cancel" />
                </Button>
              </Link>
              <div className="flex flex-row-reverse gap-4">
                <Button type="submit" className="w-[110px]">
                  <Translation id="common.page.button.submit" />
                </Button>
                <Button variant="outline" className="w-[122px]" onClick={() => setActiveStep(activeStep - 1)}>
                  <Translation id="common.page.button.previous" />
                </Button>
              </div>
            </div>
          ) : (
            <Button type="button" className="w-[110px] ml-auto" onClick={() => void handleNext()}>
              <Translation id="common.page.button.next" />
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default ConvertBasicToPremium;
